/**
 * The table: the scroller, the five cells per row, and the keyboard's way down the list.
 *
 * ── Only a WINDOW of the catalogue is in the DOM, and that is measured ──────────────────────
 * 479 movements rendered in full were 13,823 nodes on one screen — the first surface in this app
 * with hundreds of rows, where Klienci and Programy hold about twenty. In a clean Chromium a
 * pointer walk over that cost 2 frames above 33ms; under a 6× CPU throttle, which is the closest
 * proxy for the owner's own browser, it cost 45 frames above 33ms and a worst of 324ms — and the
 * owner reported stalls of seconds, identically in a production build, in incognito, with the
 * accessibility tree off and with GPU acceleration off. Every one of those amplifiers scales with
 * the node count, which is the only quantity none of them changed.
 *
 * So `@tanstack/react-virtual` mounts the rows the scroller can show plus `OVERSCAN` either side.
 * The `<div>` they stand in keeps the height of the WHOLE list, so the scrollbar still describes
 * 479 rows and a scroll position still means what it meant. `aria-rowcount` on the table and
 * `aria-rowindex` on every row say the same thing to anything that cannot see the scrollbar.
 *
 * The row memoisation and `useExerciseLibrary`'s memoised projection chain STAY: they are the other
 * half. Virtualisation makes each pointer move cheap in the reconciler, and those two keep it from
 * being a pass over the catalogue.
 *
 * ── The keyboard reaches a row that is not mounted ──────────────────────────────────────────
 * `Home`, `End`, `PageUp` and `PageDown` name a row four hundred places away, which by construction
 * is not in the DOM. So the walk is two acts rather than one: `scrollToIndex` puts the row where it
 * belongs, and `pendingFocusKey` remembers what to focus — the key is PINNED into the mounted range
 * below, so the very next render has the element and the effect focuses it. `visibleRows` still
 * measures a screenful from the row's own height and the scroller's, never from how many rows
 * happen to be mounted, which is the number virtualisation would have quietly changed.
 *
 * ── Focus and the selection are PINNED into the range, so focus cannot fall to `<body>` ─────
 * A focused row that scrolled out of the window would be unmounted under the trainer, and focus
 * would land on the document. `rangeExtractor` is the library's own answer: the selected row's
 * index, the focused one and the one focus is on its way to are added to whatever the range
 * extractor would otherwise return. It is also what makes the theatre's `finalFocus` work — the
 * walk may have carried the card three hundred movements away, and the row it returns focus to is
 * mounted because it is the selection.
 *
 * ── Pointing SELECTS, and the selection STAYS ───────────────────────────────────────────────
 * The canon's own construction, and the plan library's: moving the pointer onto a row selects it,
 * and the selection survives the pointer leaving the list entirely — so it is state rather than a
 * `:hover` rule, which would empty the panel the moment the trainer reached for the scrollbar.
 *
 * `onMouseMove`, NOT `onMouseEnter`, and that is measured rather than preferred. A dialog makes the
 * rest of the page `inert`, so no row is hoverable while one is open, and the instant it closes the
 * browser re-evaluates hover and fires `mouseenter` under a pointer that never moved — which on the
 * roster swapped the panel to a client nobody chose and dropped focus to `<body>`. The row that is
 * ALREADY selected passes no handler at all: a resting pointer fires `mousemove` sixty to a hundred
 * and twenty times a second, and every one of those was a round of work to select what was already
 * selected.
 *
 * ── The keyboard walks to the row's OPENER, and a press anywhere on the row does the same ───
 * Every row opens the exercise card. The focus target is therefore the movement's NAME, which is a
 * real `<button>` carrying `data-exercise-row`: the region holds the one tab stop, an arrow moves
 * focus to a row's opener, and focus moves the selection through `onFocusCapture` on the row.
 *
 * The click handler sits on the ROW and not on the button, and that is the whole trick. A press on
 * the equipment column has to open the card too — the canon's row is one target — and `Enter` or
 * `Space` on a focused `<button>` fires a click that BUBBLES to it. One handler, one act, and a
 * keyboard user gets an announced control with a role rather than a `role="row"` that silently
 * responds to Enter.
 *
 * ── A row is its own component, and that is measured ───────────────────────────────────────
 * `ExerciseTableRow`, memoized by the React Compiler on its props. Rendered inline in the map below
 * it, a pointer move re-reconciled all 479 rows to change a class on two — 17 frames over 33ms in a
 * 20-row walk, worst 167ms. Its own header carries the numbers and the rule that every prop handed
 * to it is a value or a stable function; `offset` and `rowIndex` are numbers for that reason, where
 * a `style` object built here would have rebuilt every mounted row on every scroll frame.
 *
 * ── No `aria-selected` ──────────────────────────────────────────────────────────────────────
 * `role="table"` has no selection in the ARIA pattern, and claiming one would promise a keyboard
 * contract this is not. The state is announced where it is TRUE instead: the panel beside the table
 * is headed by the name of the movement it is describing.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';

import type { PointedRow } from '~/shared/lib/usePointedRow';

import type { ExerciseRow } from '../model/exerciseRow';
import type { ExerciseSort, ExerciseSortColumn } from '../model/exerciseSort';

import { ExerciseTableHead } from './ExerciseTableHead';
import { ExerciseTableRow } from './ExerciseTableRow';
import scrollbar from '~/shared/ui/scrollbar.module.css';
import styles from './ExerciseTable.module.css';

interface ExerciseTableProps {
  readonly rows: readonly ExerciseRow[];
  readonly pointer: PointedRow;
  readonly sort: ExerciseSort;
  readonly onPressColumn: (column: ExerciseSortColumn) => void;
  /**
   * Whether the preview column is standing beside the table.
   *
   * ONE prop rather than a width and a cap: both follow from it, and a caller that could set them
   * apart could draw three pills in the narrow cut. Without the panel the table takes the measure,
   * widens every track and fits a THIRD muscle pill — which is the point of hiding it.
   */
  readonly panelVisible: boolean;
  /** `useSkeletonGate`'s hand-over class, for the table that replaces the skeleton. */
  readonly className?: string;
  /** Open the exercise card over the list. The address is what actually holds it open. */
  readonly onOpen: (exerciseId: number) => void;
}

/**
 * How many muscle pills fit, with the panel beside the table and without it.
 *
 * A property of the LAYOUT rather than of the movement, which is why the projection carries every
 * muscle and this is where the cap lives. The canon's own two numbers.
 */
const MUSCLE_CAP = {
  withPanel: 2,
  wide: 3,
} as const;

/**
 * How many rows stand above and below the window the scroller can show.
 *
 * Eight is about 416px either side at the canon's row height — enough that a flick of the wheel and
 * a `PageDown` both land on rows that are already painted, and small enough that the DOM holds a
 * few dozen rows rather than a few hundred. It also absorbs the one row a fractional scroll offset
 * puts half outside the window.
 */
const OVERSCAN = 8;

/**
 * The row's height and the list's own start, before anything has been measured.
 *
 * Both are the canon's numbers — `--h-ex-table-row` and the head's 38px — and both are REPLACED by
 * the measured ones in a layout effect before the first paint. They are here so the very first
 * render asks the virtualizer for a plausible window rather than for a list of height zero; a drift
 * between these and the stylesheet costs that one pre-paint frame and nothing after it.
 */
const SEED_GEOMETRY: { readonly row: number; readonly listStart: number } = {
  row: 52,
  listStart: 38,
};

/** The row element behind a mounted opener — `data-exercise-row` sits on the movement's name. */
function firstRowIn(scroller: HTMLElement): Element | null {
  return scroller.querySelector('[data-exercise-row]')?.closest('[role="row"]') ?? null;
}

/**
 * How many rows a `PageUp` / `PageDown` moves: a screenful, measured from a row's own height.
 *
 * From the HEIGHT and not from how many rows are in the DOM — the mounted window is the scroller's
 * screenful plus two lots of overscan, so counting elements would make a page a third longer than
 * the page the trainer is looking at.
 */
function visibleRows(scroller: HTMLElement): number {
  const height = firstRowIn(scroller)?.getBoundingClientRect().height ?? 0;
  if (height <= 0) return 1;
  return Math.max(1, Math.floor(scroller.clientHeight / height));
}

function keyboardSteps(key: string, page: () => number): number | null {
  switch (key) {
    case 'ArrowDown':
      return 1;
    case 'ArrowUp':
      return -1;
    case 'PageDown':
      return page();
    case 'PageUp':
      return -page();
    // Clamped by `step`, so the far end is whatever the list's length happens to be.
    case 'End':
      return Infinity;
    case 'Home':
      return -Infinity;
    default:
      return null;
  }
}

export function ExerciseTable({
  rows,
  pointer,
  sort,
  onPressColumn,
  panelVisible,
  className,
  onOpen,
}: ExerciseTableProps) {
  const { t } = useTranslation();
  const muscleCap = panelVisible ? MUSCLE_CAP.withPanel : MUSCLE_CAP.wide;

  const scrollerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [geometry, setGeometry] = useState(SEED_GEOMETRY);
  /** The row focus is on its way to, while it is not in the DOM yet. See the header. */
  const [pendingFocusKey, setPendingFocusKey] = useState<string | null>(null);
  /**
   * The row focus is on.
   *
   * Only ever CLEARED by focus moving to something that is not a row. A window losing focus reports
   * a blur with no `relatedTarget` while `document.activeElement` stays exactly where it was, and
   * clearing on that would unpin the focused row, unmount it, and hand focus to `<body>` the moment
   * the trainer came back. Holding a stale pin costs one extra mounted row.
   */
  const [focusedKey, setFocusedKey] = useState<string | null>(null);

  /**
   * The two geometries the virtualizer works in, read off the elements that draw them.
   *
   * Once, on mount: both come from a stylesheet that states them in pixels, so nothing moves them
   * afterwards — and a layout effect on every render would force a synchronous layout on every
   * pointer move, which is the cost this whole file exists to remove.
   *
   * `listStart` is how far down the scroller's own content the rows begin, which is the sticky
   * head's height. The virtualizer needs it twice: as `scrollMargin`, so the range it computes is
   * about the same coordinates the scroller reports, and as `scrollPaddingStart`, so a row scrolled
   * to the top of the list lands BELOW the head rather than underneath it.
   */
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const list = listRef.current;
    if (scroller === null || list === null) return;
    const row = firstRowIn(scroller)?.getBoundingClientRect().height ?? 0;
    const listStart =
      list.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
    const measured = {
      row: row > 0 ? row : SEED_GEOMETRY.row,
      listStart: listStart > 0 ? listStart : SEED_GEOMETRY.listStart,
    };
    setGeometry((current) =>
      current.row === measured.row && current.listStart === measured.listStart ? current : measured,
    );
  }, []);

  const indexOfKey = (key: string | null) =>
    key === null ? -1 : rows.findIndex((row) => row.rowKey === key);
  const selectedIndex = indexOfKey(pointer.selectedKey);
  const focusedIndex = indexOfKey(focusedKey);
  const pendingIndex = indexOfKey(pendingFocusKey);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollerRef.current,
    estimateSize: () => geometry.row,
    overscan: OVERSCAN,
    scrollMargin: geometry.listStart,
    scrollPaddingStart: geometry.listStart,
    // The three rows that must be in the DOM whatever the scroller is showing. See the header.
    rangeExtractor: (range) => {
      const indexes = new Set(defaultRangeExtractor(range));
      for (const index of [selectedIndex, focusedIndex, pendingIndex]) {
        if (index >= 0) indexes.add(index);
      }
      // Ascending, so the reading order assistive technology walks is the list's order — an
      // absolutely positioned row is where its offset puts it, but the accessibility tree is the
      // DOM's.
      return [...indexes].sort((left, right) => left - right);
    },
  });

  /**
   * Focus the row the keyboard asked for, now that it exists.
   *
   * `preventScroll`: `scrollToIndex` has already put the row where the canon wants it, and the
   * browser's own focus scroll would be a second opinion about the same act.
   */
  useEffect(() => {
    if (pendingFocusKey === null) return;
    const target = scrollerRef.current?.querySelector<HTMLElement>(
      `[data-exercise-row="${pendingFocusKey}"]`,
    );
    if (target === null || target === undefined) return;
    setPendingFocusKey(null);
    target.focus({ preventScroll: true });
  }, [pendingFocusKey]);

  return (
    // A scroll container needs a way IN from the keyboard: focus sits on `<body>`, the body's
    // scrollport has no overflow, and a bare `<div>` is not something a keyboard can put focus into.
    // `tabindex` + `role="region"` with a name is the ARIA practices' pattern for exactly this.
    <div
      ref={scrollerRef}
      className={[
        scrollbar.scrollbar,
        styles.scroller,
        panelVisible ? '' : styles.wide,
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      tabIndex={0}
      role="region"
      aria-label={t('web:exerciseLibrary.scrollRegion')}
      onFocusCapture={(event) => {
        const key = event.target.dataset.exerciseRow ?? null;
        setFocusedKey(key);
        // A row can take focus while it is nowhere near the window: the theatre's walk carries the
        // card across the list without moving the table behind it, and the row `finalFocus` hands
        // focus back to is wherever the walk ended — mounted, because it is the selection and the
        // selection is pinned, but four hundred rows down. Base UI returns focus with
        // `preventScroll`, so nothing else brings it into view. `align: 'auto'` is a no-op for a row
        // that is already whole on screen, which is every other way focus gets here.
        const index = key === null ? -1 : rows.findIndex((row) => row.rowKey === key);
        if (index >= 0) virtualizer.scrollToIndex(index, { align: 'auto' });
      }}
      onBlurCapture={(event) => {
        // See `focusedKey`: a blur with no `relatedTarget` leaves the pin alone on purpose.
        const next = event.relatedTarget;
        if (next !== null && next.closest('[data-exercise-row]') === null) setFocusedKey(null);
      }}
      onKeyDown={(event) => {
        const scroller = event.currentTarget;
        // Focus on the REGION's own tab stop rather than on a row. The first press then ENTERS the
        // list at the row the panel is already describing: an arrow computed from the selection
        // regardless of where focus was would skip row one entirely, and a trainer who tabbed in
        // and pressed Down would watch the panel jump to a movement they had not chosen.
        const entering = event.target === scroller;
        if (event.key === 'Escape') {
          // Out of the rows and back onto the region, which is one shift-Tab from the toolbar above.
          // A trainer forty rows down otherwise has no way back to the list's own controls.
          if (entering) return;
          event.preventDefault();
          scroller.focus();
          return;
        }
        const steps = keyboardSteps(event.key, () => visibleRows(scroller));
        if (steps === null) return;
        // Entering, every RELATIVE key lands on the selection; `Home` and `End` are absolute and
        // mean the same thing wherever focus was, which is what `Infinity` says here.
        const next = pointer.step(entering && Number.isFinite(steps) ? 0 : steps);
        if (next === null) return;
        const index = rows.findIndex((row) => row.rowKey === next);
        if (index < 0) return;
        const target = scroller.querySelector<HTMLElement>(`[data-exercise-row="${next}"]`);
        // Already there — at either end, or entering onto the row that has focus. The event is left
        // alone so the browser's own scroll still works.
        if (target === document.activeElement) return;
        event.preventDefault();
        virtualizer.scrollToIndex(index, { align: 'auto' });
        if (target === null) {
          // Four hundred rows away and therefore not mounted. Pinning the key brings it into the
          // next render, and the effect above focuses it there.
          setPendingFocusKey(next);
          return;
        }
        target.focus({ preventScroll: true });
      }}
    >
      <div
        className={styles.table}
        role="table"
        aria-label={t('web:exerciseLibrary.tableLabel')}
        // The head is a row too, which is why the count is one more than the catalogue's and every
        // movement's index is one more than its place in the list.
        aria-rowcount={rows.length + 1}
      >
        <ExerciseTableHead sort={sort} onPressColumn={onPressColumn} />

        {/* The whole list's height, whether or not a row is mounted: the scrollbar describes 479
          * rows, the reserve the skeleton stood in is replaced by a box of the same construction,
          * and nothing above or below this moves when the window inside it changes. */}
        <div
          ref={listRef}
          role="rowgroup"
          className={styles.rows}
          style={{ height: `${String(virtualizer.getTotalSize())}px` }}
        >
          {virtualizer.getVirtualItems().map((item) => {
            const row = rows[item.index];
            return row === undefined ? null : (
              <ExerciseTableRow
                key={row.rowKey}
                row={row}
                rowIndex={item.index + 2}
                offset={item.start - geometry.listStart}
                selected={row.rowKey === pointer.selectedKey}
                muscleCap={muscleCap}
                onPoint={pointer.point}
                onOpen={onOpen}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
